import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload } from 'lucide-react';

import type { FormImageUploadProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { cn } from '@/lib/utils';

import { DisplayToast } from '@/helpers';

import { IMAGE_SIZE_LIMIT_B, IMAGE_SIZE_LIMIT_MB } from '@/constants/files';

export const FormImageUpload = <TFieldValues extends FieldValues = FieldValues>(
  props: FormImageUploadProps<TFieldValues>,
) => {
  const {
    name,
    label,
    description,
    isRequired,
    url,
    hasSizeLimit = true,
    isRemovable = false,
    control,
  } = props;
  const { t } = useTranslation();
  const formContext = useFormContext<TFieldValues>();
  const formControl = control || formContext.control;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(url || null);

  const fieldId = `form-field-${String(name)}`;

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field, fieldState }) => {
        const handleFileChange = (file: File | null) => {
          if (!file) {
            field.onChange('');
            setPreviewUrl(null);
            return;
          }

          if (hasSizeLimit && file.size > IMAGE_SIZE_LIMIT_B) {
            DisplayToast.error(
              t('fileWeightMsg', { size: IMAGE_SIZE_LIMIT_MB }),
            );
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setPreviewUrl(result);
            field.onChange(file);
          };
          reader.readAsDataURL(file);
        };

        return (
          <Field data-invalid={fieldState.invalid}>
            {label && (
              <FieldLabel htmlFor={fieldId}>
                {label}
                {isRequired && (
                  <span
                    className={cn(
                      'ml-1',
                      fieldState.invalid
                        ? 'text-destructive'
                        : 'text-muted-foreground',
                    )}
                  >
                    *
                  </span>
                )}
              </FieldLabel>
            )}
            <div className="space-y-2">
              {previewUrl && (
                <div className="relative w-full">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-64 w-full rounded-md border object-contain"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  className="flex-1"
                  aria-invalid={fieldState.invalid}
                >
                  <Upload className="mr-2 size-4" />
                  {t('Choose File')}
                </Button>
                {isRemovable && previewUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-lg"
                    className="shrink-0"
                    aria-label={t('Remove File')}
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                      handleFileChange(null);
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    handleFileChange(file);
                  }}
                />
              </div>
            </div>
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </Field>
        );
      }}
    />
  );
};
